import { useCallback, useContext } from 'react';

import type { EncryptedPrivateKey, GenerateKey } from '@sorare/wallet-shared';
import { MessagingContext } from '@sorare/wallet-shared';
import { useAuthContext } from 'contexts/auth';
import type { SignedInInfo } from 'contexts/connection';
import { useConnectionContext } from 'contexts/connection';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { nullAddress } from 'lib/ethereum';
import { mustAcceptTermsOfServiceFlag } from 'lib/mustAcceptTermsOfServiceFlag';

export const useOnSignedIn = () => {
  const { sendRequest } = useContext(MessagingContext)!;
  const { updateUser } = useAuthContext();
  const { promptTerms } = useConnectionContext();
  const {
    flags: { lastTermsOfServiceUpdatedAt = '' },
  } = useFeatureFlags();

  const mustAcceptTermsFlag = mustAcceptTermsOfServiceFlag(
    lastTermsOfServiceUpdatedAt
  );

  const onSignedIn = useCallback(
    async (
      {
        email,
        wallet,
        sorarePrivateKey,
        mustAcceptTcus,
        ...rest
      }: SignedInInfo,
      passwordHash?: string
    ) => {
      let address = wallet?.ethereumAddress;
      let userPrivateKey = sorarePrivateKey;

      if (!address || address === nullAddress) {
        const { result } = await sendRequest<GenerateKey>('generateKey', {
          email,
        });

        if (result) {
          const { wallet: newWallet } = result;
          const {
            ethereumAddress,
            passwordEncryptedPrivateKey: { iv, salt, payload },
          } = newWallet;

          address = ethereumAddress;
          userPrivateKey = {
            iv,
            salt,
            encryptedPrivateKey: payload,
          };

          await updateUser({
            currentPasswordHash: passwordHash,
            ...newWallet,
          });
        }
      }
      if (mustAcceptTcus && mustAcceptTermsFlag) {
        promptTerms({
          closable: true,
        });
      }

      return {
        result: {
          ...rest,
          address: address as string,
          userPrivateKey: userPrivateKey as EncryptedPrivateKey,
          email,
        },
      };
    },
    [sendRequest, updateUser, promptTerms, mustAcceptTermsFlag]
  );
  return onSignedIn;
};
